/* ==========================================================================
   Admin Protected Route
   Task F20: Route wrapper for admin routes with pharmacy validation
   ========================================================================== */

import { Navigate, useLocation } from 'react-router-dom'
import { useAdminAuth } from '@/hooks/useAdminAuth'
import { usePortal } from '@/context/PortalContext'
import { PharmacyUserRole } from '@/api/types'

interface AdminProtectedRouteProps {
  /** The protected content to render when authenticated */
  children: React.ReactNode
  /**
   * When true, Staff are denied and redirected to the dashboard (PORTAL-94:
   * manager/admin-only sections such as Settings, Billing, Faxes, Team). The
   * backend is the authoritative guard regardless.
   */
  requireManager?: boolean
}

/**
 * Route wrapper that ensures the admin is authenticated AND belongs to the current pharmacy.
 * Redirects to admin login page if not authenticated, preserving the intended destination.
 * Shows an error if the admin belongs to a different pharmacy.
 *
 * @example
 * ```tsx
 * <Route
 *   path="admin/settings"
 *   element={
 *     <AdminProtectedRoute>
 *       <AdminSettings />
 *     </AdminProtectedRoute>
 *   }
 * />
 * ```
 */
export function AdminProtectedRoute({
  children,
  requireManager = false,
}: AdminProtectedRouteProps) {
  const { isAuthenticated, loading: authLoading, user, pharmacy } = useAdminAuth()
  const {
    slug,
    config,
    loading: portalLoading,
    error: portalError,
  } = usePortal()
  const location = useLocation()

  // Show loading state while checking authentication or loading portal config
  if (authLoading || portalLoading) {
    return <AdminAuthLoadingState />
  }

  // If portal config failed to load (invalid slug), show error
  if (portalError || !config) {
    return <PharmacyNotFoundState slug={slug} />
  }

  // Redirect to admin login if not authenticated
  if (!isAuthenticated || !user) {
    // Preserve the intended destination for redirect after login
    const redirectTo = location.pathname + location.search
    return (
      <Navigate
        to={`/rx/${slug}/admin/login?redirect=${encodeURIComponent(redirectTo)}`}
        replace
      />
    )
  }

  // Validate that admin belongs to this pharmacy
  if (pharmacy && pharmacy.id !== config.id) {
    return (
      <WrongPharmacyState
        userPharmacySlug={pharmacy.portal_slug}
        currentSlug={slug}
      />
    )
  }

  // Manager/Admin-only route: Staff are sent back to their dashboard (PORTAL-94).
  if (requireManager && user.role === PharmacyUserRole.STAFF) {
    return <Navigate to={`/rx/${slug}/admin/dashboard`} replace />
  }

  // Admin is authenticated and belongs to this pharmacy - render the protected content
  return <>{children}</>
}

/**
 * Loading state shown while checking admin authentication.
 */
function AdminAuthLoadingState() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-beige">
      <div className="flex flex-col items-center gap-4">
        {/* Spinner */}
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-lavender border-t-deep-purple" />
        <p className="text-sm text-charcoal/70">Loading admin portal...</p>
      </div>
    </div>
  )
}

/**
 * Error state shown when pharmacy slug is invalid.
 */
function PharmacyNotFoundState({ slug }: { slug: string | null }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
      <div className="max-w-md text-center">
        <div className="mb-6 text-6xl">🏥</div>
        <h1 className="mb-2 font-serif text-2xl text-charcoal">
          Pharmacy Not Found
        </h1>
        <p className="mb-6 text-charcoal/70">
          {slug ? (
            <>
              The pharmacy portal{' '}
              <span className="font-mono font-medium">{slug}</span> does not
              exist or is no longer available.
            </>
          ) : (
            'No pharmacy portal specified.'
          )}
        </p>
        <a
          href="/"
          className="inline-block rounded-lg bg-deep-purple px-6 py-2 text-white hover:bg-deep-purple/90"
        >
          Go to Home
        </a>
      </div>
    </div>
  )
}

/**
 * Error state shown when admin is logged in but to a different pharmacy.
 */
function WrongPharmacyState({
  userPharmacySlug,
  currentSlug,
}: {
  userPharmacySlug: string
  currentSlug: string | null
}) {
  // Log this for debugging
  console.warn(
    `Admin pharmacy mismatch: logged into ${userPharmacySlug}, but accessing ${currentSlug}`
  )

  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
      <div className="max-w-md text-center">
        <div className="mb-6 text-6xl">🔒</div>
        <h1 className="mb-2 font-serif text-2xl text-charcoal">
          Access Denied
        </h1>
        <p className="mb-6 text-charcoal/70">
          You are logged in to a different pharmacy portal. Please log in with
          an account registered to this pharmacy, or return to your own admin
          portal.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <a
            href={`/rx/${currentSlug}/admin/login`}
            className="inline-block rounded-lg bg-deep-purple px-6 py-2 text-white hover:bg-deep-purple/90"
          >
            Log In to This Pharmacy
          </a>
          <a
            href={`/rx/${userPharmacySlug}/admin`}
            className="inline-block rounded-lg border border-charcoal/20 px-6 py-2 text-charcoal hover:bg-charcoal/5"
          >
            Go to Your Admin Portal
          </a>
        </div>
      </div>
    </div>
  )
}

export default AdminProtectedRoute
